/**
 * SimpleMarkdown — dependency-free renderer for a tiny Markdown subset.
 *
 * Built for static, operator-authored legal text (the AVV document loaded by
 * `InboundActivation.tsx` from `public/legal/`), which only ever uses:
 * headings (`#`, `##`, `###`), bold (`**text**`), blank-line-separated
 * paragraphs, and unordered lists (`- item`). It is NOT a general-purpose
 * Markdown engine (no links, tables, nested lists, code blocks, etc.).
 *
 * Security: builds React elements directly — no `dangerouslySetInnerHTML`,
 * so there is no HTML/script injection surface even though the source file
 * is under our own control (defense in depth, not just trust-the-source).
 *
 * `react-markdown` is intentionally NOT used here (not an installed
 * dependency; adding one for a three-construct subset was judged
 * disproportionate for this scope).
 */

import React from 'react';

/** Splits a line into React nodes, turning `**bold**` spans into `<strong>`. */
function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts
    .filter((part) => part.length > 0)
    .map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
        return <strong key={`${keyPrefix}-b${i}`}>{part.slice(2, -2)}</strong>;
      }
      return <React.Fragment key={`${keyPrefix}-t${i}`}>{part}</React.Fragment>;
    });
}

interface SimpleMarkdownProps {
  /** Raw Markdown-subset source text (already loaded, not fetched here). */
  text: string;
}

/**
 * Renders a small Markdown subset (headings, bold, paragraphs, unordered
 * lists) as real DOM elements with dark-theme-appropriate styling matching
 * the surrounding `InboundActivation` card.
 */
const SimpleMarkdown: React.FC<SimpleMarkdownProps> = ({ text }) => {
  const lines = text.split(/\r?\n/);
  const blocks: React.ReactNode[] = [];
  let listBuffer: string[] = [];
  let paragraphBuffer: string[] = [];

  const flushList = (key: string) => {
    if (listBuffer.length === 0) return;
    const items = listBuffer;
    listBuffer = [];
    blocks.push(
      <ul key={key} className="list-disc pl-5 space-y-0.5 my-2">
        {items.map((item, i) => (
          <li key={`${key}-li${i}`}>{renderInline(item, `${key}-li${i}`)}</li>
        ))}
      </ul>,
    );
  };

  const flushParagraph = (key: string) => {
    if (paragraphBuffer.length === 0) return;
    const content = paragraphBuffer.join(' ');
    paragraphBuffer = [];
    blocks.push(
      <p key={key} className="my-2 first:mt-0">
        {renderInline(content, key)}
      </p>,
    );
  };

  lines.forEach((rawLine, idx) => {
    const line = rawLine.trim();
    const key = `b${idx}`;

    if (line === '') {
      flushParagraph(`p-${key}`);
      flushList(`l-${key}`);
      return;
    }

    const headingMatch = /^(#{1,3})\s+(.*)$/.exec(line);
    if (headingMatch) {
      flushParagraph(`p-${key}`);
      flushList(`l-${key}`);
      const level = headingMatch[1].length;
      const content = renderInline(headingMatch[2], key);
      if (level === 1) {
        blocks.push(
          <h3 key={key} className="text-base font-semibold text-primary mt-3 mb-1 first:mt-0">
            {content}
          </h3>,
        );
      } else if (level === 2) {
        blocks.push(
          <h4 key={key} className="text-sm font-semibold text-primary mt-3 mb-1 first:mt-0">
            {content}
          </h4>,
        );
      } else {
        blocks.push(
          <h5 key={key} className="text-sm font-medium text-secondary mt-2 mb-1 first:mt-0">
            {content}
          </h5>,
        );
      }
      return;
    }

    const listMatch = /^[-*]\s+(.*)$/.exec(line);
    if (listMatch) {
      flushParagraph(`p-${key}`);
      listBuffer.push(listMatch[1]);
      return;
    }

    flushList(`l-${key}`);
    paragraphBuffer.push(line);
  });

  flushParagraph('p-end');
  flushList('l-end');

  return <>{blocks}</>;
};

export default SimpleMarkdown;
